import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Flex, Text } from 'core/components';
import { Field, Form, Switch } from 'core/form';
import { dateRangeDisplay, DEFAULT_DATETIME_FORMAT } from 'core/util/dateUtils';
import VizWrapper from '../utils/VizWrapper';
import Graph from './graph';
import { buildGraphStructure } from './graphBuilder';
import { applyColorCoding } from './graphColorCoding';

const fields = {
  hideASName: {
    defaultValue: false
  }
};

@inject('$bgp', '$colors')
@Form({ fields, options: { name: 'Bgp Test Results AS Path Graph Options' } })
@observer
class BgpPathsGraphWrapper extends Component {
  renderGraphForm() {
    return (
      <Flex alignItems="center" gap={1}>
        <Field name="hideASName" showLabel={false} large mb={0}>
          <Switch switchLabel="Hide AS Names" large />
        </Field>
      </Flex>
    );
  }

  render() {
    const { $bgp, $colors, form, setPrefix, prefixes, selectedTimeMs, hoveredAsn, model } = this.props;
    const loading = $bgp.asPathGraphTimeseriesLoading;
    const timeseriesData = $bgp.asPathGraphTimeseriesData;

    let results = {};
    let hasResults = false;
    let snappedTimeDisplay = null;
    let selectedGraph = null;

    if (timeseriesData && selectedTimeMs) {
      const { graphs = [], routes, step_seconds, lookups } = timeseriesData;

      if (step_seconds && graphs.length > 0) {
        // Find the graph for the selected time
        const snappedTimeSec = Math.floor(selectedTimeMs / 1000 / step_seconds) * step_seconds;
        snappedTimeDisplay = dateRangeDisplay({
          starting_time: snappedTimeSec * 1000,
          ending_time: null,
          format: DEFAULT_DATETIME_FORMAT
        });

        // Find the graph with the closest timestamp <= snappedTimeSec
        // Graphs are assumed to be ordered by time
        for (let i = graphs.length - 1; i >= 0; i--) {
          const graphTimeSec = parseInt(graphs[i].timestamp?.seconds, 10);
          if (graphTimeSec <= snappedTimeSec) {
            selectedGraph = graphs[i];
            break;
          }
        }
      }

      if (selectedGraph) {
        // Build graph structure from BGP data
        const graphData = buildGraphStructure({
          selectedGraph,
          routes,
          prefixes
        });

        // Apply color coding based on BGP test configuration
        const config = model.get('config.bgp');
        results = applyColorCoding({
          config,
          graphData,
          lookups,
          chartColors: [...$colors.qualitativeColors].reverse(),
          asnColorMap: $bgp.asnColorMap
        });

        hasResults = graphData.visibleASNs.length > 0;
      }
    }

    const dataLoaded = timeseriesData !== null;
    const hasData = dataLoaded && timeseriesData.graphs?.length > 0;

    let noResultsDescription;
    if (!dataLoaded || !selectedTimeMs) {
      noResultsDescription = 'Hover over the reachability timeline above to visualize AS paths at a point in time';
    } else if (!hasData) {
      noResultsDescription = 'No AS paths were observed during this time range';
    } else if (selectedGraph) {
      noResultsDescription = 'No AS paths found for the selected prefixes at this time';
    } else {
      noResultsDescription = 'No AS paths were observed at this time';
    }

    return (
      <VizWrapper
        key="bgp-paths-graph"
        loading={loading}
        renderContent={hasResults}
        heading="AS Path Visualization"
        subtitle="Network graph showing all key AS paths associated with the monitored prefixes. Hover over any AS node or link to see more information"
        noResultsDescription={noResultsDescription}
        menu={this.renderGraphForm()}
      >
        <Graph
          {...results}
          setPrefix={setPrefix}
          hideASName={form.getValue('hideASName')}
          prefixes={prefixes}
          asn={hoveredAsn}
        />
        {snappedTimeDisplay && (
          <Flex flexDirection="column" alignItems="center" mt={2}>
            <Text small muted>
              {snappedTimeDisplay}
            </Text>
          </Flex>
        )}
      </VizWrapper>
    );
  }
}

export default BgpPathsGraphWrapper;
